import React, { useState } from 'react';

import { Button } from 'components/buttons/Button';
import { Icon } from 'components/icons/Icon';
import { Checkbox } from 'components/switches/Checkbox';
import { useThemeSafe } from 'components/theme/ThemeProvider';

import { PolicyDocumentSource } from '../../types/login';
import type { PolicyDocument, PolicyDocumentType } from '../../types/login';

// Shared by both login SPAs, so it must not depend on react-intl (the legacy SPA has no provider).
type PolicyDocumentsFormProps = {
    companyName?: string;
    logoUrl?: string | null;
    alternateLogoUrl?: string | null;
    email?: string;
    title?: string | null;
    text?: string | null;
    documents: PolicyDocument[];
    checkedTypes: PolicyDocumentType[];
    isAccepting: boolean;
    isDeclining: boolean;
    error?: React.ReactNode;
    onCheckedChange: (type: PolicyDocumentType, checked: boolean) => void;
    onAccept: () => void;
    onDecline: () => void;
};

export const PolicyDocumentsForm = ({
    companyName,
    logoUrl,
    alternateLogoUrl,
    email,
    title,
    text,
    documents,
    checkedTypes,
    isAccepting,
    isDeclining,
    error,
    onCheckedChange,
    onAccept,
    onDecline,
}: PolicyDocumentsFormProps) => {
    const theme = useThemeSafe()?.theme;
    const [hasLogoError, setHasLogoError] = useState(false);
    // Same choice as the main app's top menu: the alternate logo is the one made for dark mode.
    const companyLogoUrl = (theme === 'dark' && alternateLogoUrl) || logoUrl;
    const showLogo = Boolean(companyLogoUrl) && !hasLogoError;
    const isBusy = isAccepting || isDeclining;
    const canAccept = documents.length > 0 && documents.every((d) => checkedTypes.includes(d.type));
    const defaultText = `Please read and accept ${
        companyName ? `${companyName}'s` : "your company's"
    } terms to continue to SalesScreen.`;

    return (
        <div className="tw-rounded-large tw-border tw-border-neutral tw-bg-neutral tw-shadow-xs">
            {showLogo ? (
                <div className="tw-flex tw-justify-center tw-border-b tw-border-neutral tw-px-24 sm:tw-px-32 tw-py-24">
                    <img
                        src={companyLogoUrl}
                        alt={companyName ?? ''}
                        className="tw-block tw-h-auto tw-max-h-48 tw-w-auto tw-max-w-[200px] tw-object-contain"
                        onError={() => setHasLogoError(true)}
                    />
                </div>
            ) : null}
            <div className="tw-px-24 sm:tw-px-32 tw-pt-32">
                {showLogo ? null : (
                    <div className="tw-flex tw-items-center tw-justify-center tw-h-48 tw-w-48 tw-rounded-full tw-bg-primary-subdued tw-text-primary tw-mb-16">
                        <Icon icon="shield-tick" size="large" />
                    </div>
                )}
                <h1 className="tw-text-size-7 tw-font-bold tw-tracking-x-tight tw-m-0 tw-mb-8">
                    {title || 'Terms and policies'}
                </h1>
                {/* Company-provided plain text: never rendered as HTML, line breaks kept */}
                <p className="tw-text-l tw-text-neutral tw-whitespace-pre-line tw-m-0">
                    {text || defaultText}
                </p>
            </div>

            <ul className="tw-list-none tw-m-0 tw-px-24 sm:tw-px-32 tw-pt-24 tw-flex tw-flex-col tw-gap-12">
                {documents.map((document) => {
                    const isChecked = checkedTypes.includes(document.type);
                    const isLink = document.source === PolicyDocumentSource.Link;

                    return (
                        <li
                            key={document.type}
                            className={`tw-rounded-large tw-border tw-transition-colors ${
                                isChecked
                                    ? 'tw-border-primary tw-bg-primary-subdued'
                                    : 'tw-border-neutral'
                            }`}
                        >
                            <div className="tw-flex tw-items-center tw-gap-12 tw-p-16">
                                <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-h-48 tw-w-48 tw-rounded-large tw-bg-neutral-variant tw-text-primary">
                                    <Icon icon={isLink ? 'globe' : 'document-filled'} />
                                </div>
                                <div className="tw-flex tw-flex-col tw-min-w-0">
                                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-8">
                                        <span className="tw-text-l tw-font-bold">
                                            {document.title}
                                        </span>
                                        {document.isUpdate ? (
                                            <span className="tw-rounded tw-bg-warning-subdued tw-text-warning tw-text-xs tw-font-bold tw-uppercase tw-px-4 tw-py-2">
                                                Updated
                                            </span>
                                        ) : null}
                                    </div>
                                    <span className="tw-text-m tw-text-neutral-subdued">
                                        {isLink ? 'Web page' : 'PDF document'}
                                    </span>
                                </div>
                                <a
                                    href={document.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="tw-ml-auto tw-flex tw-shrink-0 tw-items-center tw-gap-4 tw-rounded tw-border tw-border-neutral tw-px-12 tw-py-4 tw-text-m tw-font-medium tw-text-primary hover:tw-border-primary"
                                >
                                    Open
                                    <Icon icon="link-external" size="small" />
                                </a>
                            </div>
                            <div className="tw-border-t tw-border-neutral tw-px-16 tw-py-12">
                                <Checkbox
                                    checked={isChecked}
                                    label={document.checkboxLabel}
                                    name={`acceptPolicyDocument${document.type}`}
                                    onChange={(e) =>
                                        onCheckedChange(document.type, e.target.checked)
                                    }
                                />
                            </div>
                        </li>
                    );
                })}
            </ul>

            {error ? <div className="tw-px-24 sm:tw-px-32 tw-pt-12">{error}</div> : null}

            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-16 tw-mt-24 tw-border-t tw-border-neutral tw-px-24 sm:tw-px-32 tw-py-16">
                {email ? (
                    <p className="tw-text-m tw-text-neutral tw-m-0">
                        Signed in as <span className="tw-font-bold">{email}</span>
                    </p>
                ) : (
                    <span />
                )}
                <div className="tw-flex tw-gap-8 tw-ml-auto">
                    <Button
                        text="Decline"
                        variant="secondary"
                        onClick={onDecline}
                        disabled={isBusy}
                    />
                    <Button
                        text="Accept and continue"
                        variant="primary"
                        onClick={onAccept}
                        disabled={!canAccept || isBusy}
                    />
                </div>
            </div>
        </div>
    );
};
